import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { gateEntryService } from '../services/gateEntryService';
import { grnService } from '../services/grnService';

interface GateEntryFilters {
  search?: string;
  shipmentType?: 'inward' | 'outward';
  status?: string;
  limit?: number;
  page?: number;
}

// Regular query for gate entries
export const useGateEntries = (filters?: GateEntryFilters) => {
  return useQuery({
    queryKey: ['gateEntries', filters],
    queryFn: () => gateEntryService.getGateEntries(filters),
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

// Infinite query for gate entries with pagination
export const useInfiniteGateEntries = (filters?: Omit<GateEntryFilters, 'page'>) => {
  return useInfiniteQuery({
    queryKey: ['gateEntries', 'list', filters],
    queryFn: ({ pageParam = 1 }) => 
      gateEntryService.getGateEntries({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const meta = lastPage.data?.meta;
      if (!meta) return undefined;
      return meta.page < meta.totalPages ? meta.page + 1 : undefined;
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

// Mutation for assigning dock
export const useAssignDock = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, dockNumber }: { id: string; dockNumber: string }) =>
      gateEntryService.assignDock(id, dockNumber),
    onSuccess: () => {
      // Invalidate gate entries list to refetch
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
    },
  });
};

// Mutation for creating GRN
export const useCreateGRN = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { gateEntryId: number }) => grnService.createGrn(data),
    onSuccess: () => {
      // Invalidate gate entries list to refetch
      queryClient.invalidateQueries({ queryKey: ['gateEntries'] });
      // Invalidate GRN list if it exists
      queryClient.invalidateQueries({ queryKey: ['grn'] });
    },
  });
};
